import { useState } from "react";
import { useNavigate } from "react-router-dom";
import Page from "src/layouts/Page";
import ContentTable, {
	ContentTableItem
} from "src/components/ContentTable/ContentTable";
import { ConfirmationModal } from "src/modals/ConfirmationModal/ConfirmationModal";
import useActiveFullScreenTakeovers from "../../hooks/useActiveFullScreenTakeovers/useActiveFullScreenTakeovers";
import useDeleteFullScreenTakeoverBanners from "../../hooks/useDeleteFullScreenTakeoverBanners/useDeleteFullScreenTakeoverBanners";
import useFullScreenTakeoverBannerLibrary from "../../hooks/useFullScreenTakeoverBannerLibrary/useFullScreenTakeoverBannerLibrary";
import useMakeFullScreenTakeoverBannersActive from "../../hooks/useMakeFullScreenTakeoverBannersActive/useMakeFullScreenTakeoverBannersActive";
import useMakeFullScreenTakeoverBannersInactive from "../../hooks/useMakeFullScreenTakeoverBannersInactive/useMakeFullScreenTakeoverBannerInactive";

function mapBannersToContentTableItems(
	activeBanners: Banner[],
	libraryBanners: Banner[]
): ContentTableItem[] {
	const live = activeBanners.map((b, index) => ({
		id: b.id,
		name: b.name,
		createdBy: b.createdBy
			? `${b.createdBy.firstName} ${b.createdBy.lastName}`
			: undefined,
		lastUpdated: b.updatedAt,
		isActive: true,
		order: index + 1
	}));

	const draft = libraryBanners.map((b) => ({
		id: b.id,
		name: b.name,
		createdBy: b.createdBy
			? `${b.createdBy.firstName} ${b.createdBy.lastName}`
			: undefined,
		lastUpdated: b.updatedAt ?? b.createdAt,
		isActive: false
	}));

	return [...live, ...draft];
}

const FullScreenTakeover = () => {
	const navigate = useNavigate();
	const [activeBanners, setActiveBanners] = useState<Banner[]>([]);
	const [bannerLibrary, setBannerLibrary] = useState<Banner[]>([]);
	const [itemToDelete, setItemToDelete] = useState<ContentTableItem | null>(
		null
	);

	useFullScreenTakeoverBannerLibrary({ onSuccess: setBannerLibrary });
	useActiveFullScreenTakeovers({ onSuccess: setActiveBanners });

	const { mutate: makeBannersActive } =
		useMakeFullScreenTakeoverBannersActive();

	const { mutate: makeBannersInactive } =
		useMakeFullScreenTakeoverBannersInactive();

	const { mutate: deleteCarouselBanners, isLoading: isDeletingBanners } =
		useDeleteFullScreenTakeoverBanners();

	const items = mapBannersToContentTableItems(activeBanners, bannerLibrary);

	return (
		<Page>
			<ContentTable
				items={items}
				onRowClick={(item) =>
					navigate(
						`/vendor-sponsorship/full-screen-takeover/banners/new-full-page-takeover-form/${item.id}`
					)
				}
				onMakeLive={(item) => makeBannersActive([Number(item.id)])}
				onMakeDraft={(item) => makeBannersInactive([Number(item.id)])}
				onDelete={(item) => setItemToDelete(item)}
				canDelete={(item) => !item.isActive}
				createPath="/vendor-sponsorship/full-screen-takeover/banners/new-full-page-takeover-form"
				createLabel="Create Banner"
			/>

			<ConfirmationModal
				isOpen={itemToDelete !== null}
				title="Delete banner?"
				description="This action cannot be undone."
				onCancel={() => setItemToDelete(null)}
				onSubmit={() => {
					if (itemToDelete) {
						deleteCarouselBanners([Number(itemToDelete.id)]);
						setItemToDelete(null);
					}
				}}
				submitBtnProps={{ isLoading: isDeletingBanners }}
			/>
		</Page>
	);
};

export default FullScreenTakeover;
